import { useState } from "react";

import { cockedHat, fixQuality, type Bearing } from "@nav-games/pilotage";

import { ThreePointFixFigure } from "./ThreePointFixFigure";

/// Three-point-fix calculator — enter 3 charted marks (lat / lon) +
/// the true bearing observed to each, get the cocked-hat triangle,
/// the centroid position and a quality rating.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 38,
  padding: "8px 10px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function Field({
  label,
  value,
  onChange,
  step = 0.0001,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step?: number;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minWidth: 80,
      }}
    >
      <span className="small muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
    </label>
  );
}

interface MarkInput {
  label: string;
  lat: string;
  lon: string;
  bearing: string;
}

function fmtLat(deg: number): string {
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const m = (abs - d) * 60;
  return `${d}° ${m.toFixed(2)}'${deg >= 0 ? "N" : "S"}`;
}
function fmtLon(deg: number): string {
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const m = (abs - d) * 60;
  return `${d}° ${m.toFixed(2)}'${deg >= 0 ? "E" : "W"}`;
}

export function ThreePointFixCalculator() {
  // Defaults — three plausible Solent marks around a point near
  // Cowes, giving a tight cocked hat.
  const [marks, setMarks] = useState<MarkInput[]>([
    {
      label: "Egypt Pt",
      lat: "50.7700",
      lon: "-1.3050",
      bearing: "315",
    },
    {
      label: "Calshot Spit",
      lat: "50.7900",
      lon: "-1.3050",
      bearing: "030",
    },
    {
      label: "Hill Hd",
      lat: "50.7750",
      lon: "-1.3300",
      bearing: "260",
    },
  ]);

  const updateMark = (i: number, patch: Partial<MarkInput>) => {
    setMarks((ms) => ms.map((m, j) => (i === j ? { ...m, ...patch } : m)));
  };

  const parsed = marks.map((m) => ({
    label: m.label,
    lat: Number(m.lat),
    lon: Number(m.lon),
    bearing: Number(m.bearing),
  }));
  const allValid = parsed.every(
    (p) =>
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lon) &&
      Number.isFinite(p.bearing),
  );

  let fix: ReturnType<typeof cockedHat> | null = null;
  let bearings: Bearing[] = [];
  if (allValid) {
    bearings = parsed.map((p) => ({
      mark: { label: p.label, latDeg: p.lat, lonDeg: p.lon },
      trueDeg: p.bearing,
    }));
    fix = cockedHat(bearings);
  }
  const quality = fix && fix.valid ? fixQuality(fix.meanSideNm) : null;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Three-point fix (cocked hat)</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Three bearings → three position lines → a triangle. Enter the
        charted lat/lon of each mark and the true bearing observed to
        it. The calculator plots the cocked hat, picks the centroid as
        the best estimate, and rates the fix quality.
      </p>

      {marks.map((m, i) => (
        <div
          key={i}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 10,
            background: "var(--bg-elev)",
          }}
        >
          <div className="small muted" style={{ marginBottom: 4 }}>
            Mark {i + 1}
          </div>
          <input
            type="text"
            value={m.label}
            onChange={(e) => updateMark(i, { label: e.target.value })}
            style={{ ...inputStyle, marginBottom: 6 }}
            aria-label={`Mark ${i + 1} label`}
          />
          <div
            style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
          >
            <Field
              label="Latitude (°)"
              value={m.lat}
              onChange={(v) => updateMark(i, { lat: v })}
            />
            <Field
              label="Longitude (°)"
              value={m.lon}
              onChange={(v) => updateMark(i, { lon: v })}
            />
            <Field
              label="True bearing (°)"
              value={m.bearing}
              onChange={(v) => updateMark(i, { bearing: v })}
              step={0.5}
            />
          </div>
        </div>
      ))}

      {fix && fix.valid ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 8,
              marginBottom: 12,
              background: "var(--bg-elev)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            <ThreePointFixFigure
              marks={parsed.map((p) => ({
                label: p.label,
                latDeg: p.lat,
                lonDeg: p.lon,
              }))}
              bearings={bearings}
              fix={fix}
              size={360}
            />
          </div>

          <div
            style={{
              padding: 10,
              marginBottom: 12,
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--accent)",
              background: "var(--accent-soft)",
            }}
          >
            <div className="small muted" style={{ marginBottom: 4 }}>
              Best-estimate fix (centroid of cocked hat)
            </div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>
              {fmtLat(fix.centroid.latDeg)} · {fmtLon(fix.centroid.lonDeg)}
            </div>
            <div className="small muted" style={{ marginTop: 4 }}>
              Cocked-hat mean side: {fix.meanSideNm.toFixed(3)} nm
              {quality && (
                <>
                  {" "}·{" "}
                  <strong style={{ color: "var(--accent)" }}>
                    {quality.label}
                  </strong>
                </>
              )}
            </div>
          </div>

          <p className="small muted" style={{ margin: 0 }}>
            Pick three marks roughly 60° apart for the best geometry —
            two marks at 90° + a third at 90° to those gives the
            tightest cocked hat for a given bearing accuracy. Avoid
            marks more than ~5 nm away (bearings degrade with range)
            and avoid marks in a near-straight line (the position
            lines become parallel and the fix is unstable).
          </p>
        </>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter three valid marks + bearings. Bearings should be true
          (apply your compass + variation correction first).
        </p>
      )}
    </div>
  );
}
